import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const statusOptions = [
  { value: 'not started', label: 'not started' },
  { value: 'ongoing', label: 'ongoing' },
  { value: 'completed', label: 'completed' },
];

const addMonths = (dateStr, months) => {
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    const target = new Date(year, month + months, day);
    if (target.getMonth() !== ((month + months) % 12 + 12) % 12) {
      target.setDate(0);
    }
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = String(target.getDate()).padStart(2, '0');
    return `${target.getFullYear()}-${mm}-${dd}`;
  } catch (err) {
    console.error('addMonths failed', err);
    return '';
  }
};

const addDays = (dateStr, days) => {
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  } catch (err) {
    console.error('addDays failed', err);
    return '';
  }
};

const diffDays = (startStr, endStr) => {
  const a = new Date(`${startStr}T00:00:00`);
  const b = new Date(`${endStr}T00:00:00`);
  const ms = Math.max(0, b.getTime() - a.getTime());
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

const monthsApprox = (startStr, endStr) => {
  const days = diffDays(startStr, endStr);
  return Math.round((days / 30.4375) * 10) / 10;
};

const computeSemesterRanges = (startDateStr, endDateStr, semesters) => {
  const ranges = [];
  if (!startDateStr || !endDateStr || !semesters || semesters < 1) return ranges;
  const start = new Date(`${startDateStr}T00:00:00`);
  const end = new Date(`${endDateStr}T00:00:00`);
  const totalMs = Math.max(0, end.getTime() - start.getTime());
  const perSemMs = semesters > 0 ? Math.floor(totalMs / semesters) : 0;
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  for (let s = 1; s <= semesters; s += 1) {
    const sStart = new Date(start.getTime() + perSemMs * (s - 1));
    const sEnd = s === semesters ? end : new Date(start.getTime() + perSemMs * s);
    ranges.push({ sem: s, start: fmt(sStart), end: fmt(sEnd) });
  }
  return ranges;
};

export default function AcademicCalendar() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);

  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [courseFilter, setCourseFilter] = useState([]);

  const [showSubjectsModal, setShowSubjectsModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchSubjects, setBatchSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBatchForAssign, setSelectedBatchForAssign] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [courseSubjects, setCourseSubjects] = useState([]);
  const [assignRows, setAssignRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [calcStartDate, setCalcStartDate] = useState('');
  const [calcMonths, setCalcMonths] = useState('');
  const [calcEndDate, setCalcEndDate] = useState('');
  const [calcSemesters, setCalcSemesters] = useState('');
  const [calcApplied, setCalcApplied] = useState(false);
  const [showSemCalcModal, setShowSemCalcModal] = useState(false);
  const [semCalcMode, setSemCalcMode] = useState('weekend');
  const [semCalcClassesPerWeek, setSemCalcClassesPerWeek] = useState(2);

  const accessibleCourseSet = useMemo(() => {
    if (hasFullAccess) return null;
    const ids = (accessibleCourseIds || []).map((id) => Number(id)).filter((id) => Number.isInteger(id));
    return new Set(ids);
  }, [accessibleCourseIds, hasFullAccess]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [accessRes, batchesRes] = await Promise.all([
        axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} })),
        axiosInstance.get('/admin/batches'),
      ]);

      const accessData = accessRes?.data || {};
      setCoordinateCourses(accessData.coordinateCourses || {});
      setHasFullAccess(Boolean(accessData.isSuperAdmin));
      setAccessibleCourseIds(accessData.accessibleCourseIds || []);

      const batchData = batchesRes?.data || {};
      setBatches(batchData.batches || []);
      setCourses(batchData.courses || []);
    } catch (err) {
      console.error('Failed to load academic calendar data', err);
      setError(err?.response?.data?.message || 'Failed to load academic calendar data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!calcStartDate) {
      setCalcEndDate('');
      return;
    }
    const months = typeof calcMonths === 'number' ? calcMonths : parseInt(String(calcMonths || '0'), 10);
    if (!months || months < 1) {
      setCalcEndDate('');
      return;
    }
    const computed = addMonths(calcStartDate, months);
    setCalcEndDate(computed);
  }, [calcStartDate, calcMonths]);

  const availableCourses = useMemo(() => {
    const map = new Map();
    batches.forEach((b) => {
      const c = b.course || {};
      const courseId = Number(c.courseId || b.courseId);
      if (!courseId) return;
      if (!hasFullAccess && accessibleCourseSet && !accessibleCourseSet.has(courseId)) return;
      if (!map.has(courseId)) {
        map.set(courseId, {
          courseId,
          shortName: c.shortName || b.courseShortName || '',
          fullName: c.fullName || b.courseFullName || '',
          scheduleType: c.scheduleType || b.scheduleType || null,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.shortName || a.fullName).localeCompare(b.shortName || b.fullName));
  }, [accessibleCourseSet, batches, hasFullAccess]);

  const courseOptions = useMemo(() => availableCourses.map((course) => {
    const courseName = course.shortName || course.fullName;
    return {
      value: String(course.courseId),
      label: courseName,
      courseName,
      scheduleType: course.scheduleType || null,
    };
  }), [availableCourses]);

  const filteredBatches = useMemo(() => {
    let list = batches;

    if (!hasFullAccess && accessibleCourseSet) {
      list = list.filter((b) => accessibleCourseSet.has(Number(b.course?.courseId || b.courseId)));
    }

    if (courseFilter.length > 0) {
      const ids = courseFilter.map((id) => Number(id));
      list = list.filter((b) => ids.includes(Number(b.course?.courseId || b.courseId)));
    }

    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((b) => [
        b.batchName,
        b.batchInternalName || '',
        b.status || '',
        b.course?.shortName || b.courseShortName || '',
        b.course?.fullName || b.courseFullName || '',
        b.whatsappGroupName || '',
      ].join(' ').toLowerCase().includes(term));
    }

    return list;
  }, [accessibleCourseSet, batches, courseFilter, hasFullAccess, search]);

  const totalPages = Math.max(1, Math.ceil(filteredBatches.length / rowsPerPage));
  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * rowsPerPage;
  const end = start + rowsPerPage;
  const pageRows = filteredBatches.slice(start, end);

  const toggleSelectAll = () => {
    if (assignRows.length === 0) return;
    const allSelected = assignRows.every((r) => selectedIds.has(r.courseSubjectId));
    setSelectedIds(allSelected ? new Set() : new Set(assignRows.map((r) => r.courseSubjectId)));
  };

  const toggleSelectRow = (courseSubjectId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(courseSubjectId)) next.delete(courseSubjectId); else next.add(courseSubjectId);
      return next;
    });
  };

  const fetchBatchSubjects = async (batch) => {
    setSelectedBatch(batch);
    setShowSubjectsModal(true);
    setLoadingSubjects(true);
    try {
      const resp = await axiosInstance.get(`/admin/batches/${batch.batchId}/subjects`);
      const data = resp?.data || {};
      if (data.success) {
        setBatchSubjects(data.subjects || []);
      } else {
        setBatchSubjects([]);
      }
    } catch (err) {
      console.error('Error fetching batch subjects', err);
      setBatchSubjects([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load batch subjects',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoadingSubjects(false);
    }
  };

  const NETWORK_ENG_ORDER = [
    'PRO',
    'NET',
    'PP',
    'DDD',
    'Security',
    'PACP',
    'Maths',
    'CSA',
    'CRP - I',
    'BPS',
    'TND',
    'CC',
    'NS',
    'IOT',
    'ET',
    'CRP - II',
  ];

  const SOFTWARE_ENG_ORDER = [
    'PRO',
    'NET',
    'PP',
    'DDD',
    'Security',
    'PACP',
    'SDLC',
    'WDD',
    'CRP - I',
    'BPS',
    'SAD',
    'UEID',
    'DM',
    'DSA',
    'APDP',
    'CRP - II',
  ];

  const L3_DIP_IT_ORDER = [
    'ITC',
    'MS',
    'CH & NW',
    'INT & WD',
    'GM',
    'AI',
    'SE',
    'Python',
    'Database',
    'C#',
  ];

  const getSubjectOrderIndex = (courseName, subjectName) => {
    if (!subjectName) return Number.MAX_SAFE_INTEGER;
    const name = String(subjectName).toLowerCase();
    const course = String(courseName || '').toLowerCase();

    let orderList = null;
    if (course.includes('network engineering')) {
      orderList = NETWORK_ENG_ORDER;
    } else if (course.includes('software engineering')) {
      orderList = SOFTWARE_ENG_ORDER;
    } else if (course.includes('l3 diploma in it') || course.includes('level 3 diploma in information technology')) {
      orderList = L3_DIP_IT_ORDER;
    }

    if (!orderList) return Number.MAX_SAFE_INTEGER;

    const idx = orderList.findIndex((label) => name.startsWith(String(label).toLowerCase()));
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };

  const prepareAssignRows = (subjects, existing) => {
    const existingMap = new Map((existing || []).map((subj) => [subj.courseSubjectId, subj]));
    return subjects.map((s) => {
      const current = existingMap.get(s.courseSubjectId);
      return {
        courseSubjectId: s.courseSubjectId,
        subjectName: s.shortName || s.fullName,
        staffId: current?.staffId || null,
        startDate: current?.startDate || '',
        endDate: current?.endDate || '',
        status: current?.status || 'not started',
        expectedNumberOfClasses: current?.expectedNumberOfClasses ?? s.numberOfClasses ?? null,
        semester: current?.semester || 1,
        assignmentReleaseDate: current?.assignmentReleaseDate || null,
        assignmentSubmissionDate: current?.assignmentSubmissionDate || null,
        assignmentLateSubmissionDate: current?.assignmentLateSubmissionDate || null,
      };
    });
  };

  const openAssignModal = async (batch) => {
    setSelectedBatchForAssign(batch);
    setShowAssignModal(true);
    setAssignLoading(true);
    try {
      const courseId = batch.course?.courseId || batch.courseId;
      const [subjectsRes, staffRes, existingRes] = await Promise.all([
        axiosInstance.get(`/admin/course-subjects/${courseId}/subjects`),
        axiosInstance.get('/admin/batches/staff-options', { params: { status: 'active' } }),
        axiosInstance.get(`/admin/batches/${batch.batchId}/subjects`),
      ]);

      const subjectsJson = subjectsRes?.data || {};
      const staffJson = staffRes?.data || {};
      const existingJson = existingRes?.data || {};

      if (subjectsJson.success) {
        setCourseSubjects(subjectsJson.subjects || []);
        const hasExisting = Array.isArray(existingJson.subjects) && existingJson.subjects.length > 0;
        const courseName = batch.course?.fullName || batch.courseFullName || batch.course?.shortName || batch.courseShortName || '';
        let rows = prepareAssignRows(subjectsJson.subjects || [], existingJson.subjects || []);

        if (hasExisting) {
          // When data already exists, order primarily by semester, then by
          // course-specific subject order (if applicable), then by name.
          rows = rows.slice().sort((a, b) => {
            const sa = Number(a.semester) || 0;
            const sb = Number(b.semester) || 0;
            if (sa !== sb) return sa - sb;

            const oa = getSubjectOrderIndex(courseName, a.subjectName);
            const ob = getSubjectOrderIndex(courseName, b.subjectName);
            if (oa !== ob) return oa - ob;

            return String(a.subjectName || '').localeCompare(String(b.subjectName || ''));
          });
        } else {
          // No existing data: keep backend order for most courses, but
          // for specific courses, order by the custom lists.
          const courseLower = String(courseName).toLowerCase();
          if (
            courseLower.includes('network engineering')
            || courseLower.includes('software engineering')
            || courseLower.includes('l3 diploma in it')
            || courseLower.includes('level 3 diploma in information technology')
          ) {
            rows = rows.slice().sort((a, b) => {
              const oa = getSubjectOrderIndex(courseName, a.subjectName);
              const ob = getSubjectOrderIndex(courseName, b.subjectName);
              if (oa !== ob) return oa - ob;
              return String(a.subjectName || '').localeCompare(String(b.subjectName || ''));
            });
          }
        }

        setAssignRows(rows);
        setSelectedIds(new Set(rows.map((r) => r.courseSubjectId)));
      } else {
        setCourseSubjects([]);
        setAssignRows([]);
        setSelectedIds(new Set());
      }

      setStaffOptions(Array.isArray(staffJson.staff) ? staffJson.staff : []);
      setCalcStartDate('');
      setCalcEndDate('');
      setCalcMonths('');
      setCalcSemesters('');
      setCalcApplied(false);
    } catch (err) {
      console.error('Failed to load assign data', err);
      setCourseSubjects([]);
      setAssignRows([]);
      setStaffOptions([]);
      setSelectedIds(new Set());
      Swal.fire({
        icon: 'error',
        title: 'Load failed',
        text: err?.response?.data?.message || 'Unable to load subjects/staff',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3500,
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const updateRowsBySemesters = (semesters, endDateOverride) => {
    if (!calcStartDate || (!calcEndDate && !endDateOverride) || !semesters || semesters < 1 || assignRows.length === 0) return;

    const overallEnd = endDateOverride || calcEndDate;
    const ranges = computeSemesterRanges(calcStartDate, overallEnd, semesters);

    setAssignRows((prev) => prev.map((row) => {
      const semNum = Number(row.semester);
      const range = ranges[semNum - 1];

      // If semester is invalid or out of range, clear dates for this row.
      if (!semNum || !range) {
        return {
          ...row,
          startDate: '',
          endDate: '',
          assignmentReleaseDate: null,
          assignmentSubmissionDate: null,
          assignmentLateSubmissionDate: null,
        };
      }

      const release = addMonths(range.start, 1);
      const submission = addDays(range.end, 25);
      const late = addDays(submission, 14);

      return {
        ...row,
        startDate: range.start,
        endDate: range.end,
        assignmentReleaseDate: release || null,
        assignmentSubmissionDate: submission || null,
        assignmentLateSubmissionDate: late || null,
      };
    }));
  };

  // Note: We now only apply semester-based date calculations when the user
  // clicks the "Apply calculation" button, not automatically when the
  // number of semesters field changes.

  const applyCalculation = () => {
    if (!calcStartDate) {
      Swal.fire({ icon: 'warning', title: 'Start date required', text: 'Please select a start date first.', toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 });
      return;
    }
    const months = typeof calcMonths === 'number' ? calcMonths : parseInt(String(calcMonths || '0'), 10);
    const semesters = typeof calcSemesters === 'number' ? calcSemesters : parseInt(String(calcSemesters || '0'), 10);
    if (!months || months < 1) {
      Swal.fire({ icon: 'warning', title: 'Invalid months', text: 'Enter number of months (> 0).', toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 });
      return;
    }
    const computedEnd = addMonths(calcStartDate, months);
    setCalcEndDate(computedEnd);

    const n = assignRows.length;
    if (n === 0) { setCalcApplied(true); return; }

    if (semesters && semesters > 0) {
      updateRowsBySemesters(semesters, computedEnd);
    } else {
      const release = addMonths(calcStartDate, 1);
      const submission = addDays(computedEnd, 25);
      const late = addDays(submission, 14);
      setAssignRows((prev) => prev.map((r) => ({
        ...r,
        startDate: calcStartDate,
        endDate: computedEnd,
        assignmentReleaseDate: release || null,
        assignmentSubmissionDate: submission || null,
        assignmentLateSubmissionDate: late || null,
      })));
    }
    setCalcApplied(true);
  };

  const saveAssignments = async () => {
    if (!selectedBatchForAssign || assignRows.length === 0) return;
    if (selectedIds.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No subjects selected',
        text: 'Please select at least one subject to save.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }
    const rowsToSave = assignRows.filter((r) => selectedIds.has(r.courseSubjectId));
    const firstInvalid = rowsToSave.find((r) => !r.startDate || !r.endDate || !r.status || !r.semester);
    if (firstInvalid) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing required fields',
        text: 'Please fill Start Date, End Date, Status and Semester for all selected subjects.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }
    const batchId = selectedBatchForAssign.batchId;
    try {
      const payload = {
        subjects: rowsToSave.map((r) => ({
          courseSubjectId: r.courseSubjectId,
          staffId: r.staffId,
          startDate: r.startDate,
          endDate: r.endDate,
          status: r.status,
          expectedNumberOfClasses: r.expectedNumberOfClasses,
          semester: r.semester,
          assignmentReleaseDate: r.assignmentReleaseDate,
          assignmentSubmissionDate: r.assignmentSubmissionDate,
          assignmentLateSubmissionDate: r.assignmentLateSubmissionDate,
        })),
      };
      const res = await axiosInstance.post(`/admin/batches/${batchId}/subjects/assign`, payload);
      const json = res?.data;
      if (json?.success) {
        Swal.fire({ icon: 'success', title: 'Saved', text: 'Assignments saved successfully', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        setShowAssignModal(false);
        setSelectedBatchForAssign(null);
        loadData();
      } else {
        Swal.fire({ icon: 'error', title: 'Save failed', text: json?.message || 'Unable to save assignments', toast: true, position: 'top-end', showConfirmButton: false, timer: 4000 });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.response?.data?.message || err?.message || 'Unable to save assignments', toast: true, position: 'top-end', showConfirmButton: false, timer: 4000 });
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    const result = await Swal.fire({
      title: 'Delete Subject?',
      text: 'This will remove the subject assignment from this batch. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/admin/batches/batch-subjects/${subjectId}`);
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Subject removed', toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 });
      if (selectedBatch) fetchBatchSubjects(selectedBatch);
    } catch (err) {
      console.error('Failed to delete subject', err);
      Swal.fire({ icon: 'error', title: 'Delete failed', text: err?.response?.data?.message || 'Unable to delete subject', toast: true, position: 'top-end', showConfirmButton: false, timer: 3500 });
    }
  };

  const downloadAsExcel = () => {
    if (batchSubjects.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no subjects to download',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const headers = [
      '#',
      'Subject Name',
      'Staff',
      'Start Date',
      'End Date',
      'Status',
      'Expected Classes',
      'Semester',
      'Assignment Release Date',
      'Assignment Submission Date',
      'Assignment Late Submission Date',
    ];

    const rows = batchSubjects.map((subject, index) => [
      index + 1,
      subject.subjectName,
      subject.staffName || '—',
      subject.startDate,
      subject.endDate,
      subject.status,
      subject.expectedNumberOfClasses,
      subject.semester,
      subject.assignmentReleaseDate || '—',
      subject.assignmentSubmissionDate || '—',
      subject.assignmentLateSubmissionDate || '—',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => {
        const cellStr = String(cell ?? '');
        if (cellStr.includes(',') || cellStr.includes('"')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `${selectedBatch?.batchName || 'Batch'}_Subjects_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Downloaded!',
      text: 'Subjects list has been downloaded',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Academic Calendar">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent" />
            <p className="mt-4 text-sm text-gray-600">Loading academic calendar...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Academic Calendar">
      {error && <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800">{error}</div>}

      <div className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Academic Calendar</h1>
            <p className="mt-1 text-sm text-gray-600">Plan and review important academic dates and milestones.</p>
          </div>

          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Rows per page</label>
                <select
                  className="rounded-lg border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <label className="ml-4 text-sm font-medium text-gray-700">Course Filter</label>
                <div className="min-w-[250px]">
                  <Select
                    isMulti
                    value={courseOptions.filter((opt) => courseFilter.includes(opt.value))}
                    onChange={(selected) => {
                      const values = selected ? selected.map((s) => s.value) : [];
                      setCourseFilter(values);
                      setCurrentPage(1);
                    }}
                    options={courseOptions}
                    className="text-sm"
                    classNamePrefix="select"
                    placeholder={hasFullAccess ? 'Select courses...' : 'Select your courses...'}
                    isClearable
                    formatOptionLabel={(option) => (
                      <div className="flex items-center gap-2">
                        <span>{option.courseName}</span>
                        {option.scheduleType && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            option.scheduleType === 'weekend'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {option.scheduleType}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="w-full md:w-96">
                <input
                  type="text"
                  placeholder="Search batches..."
                  className="w-full rounded-lg border-gray-300 px-4 py-2.5 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Batch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">WhatsApp Group</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Count</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">No batches found</td>
                    </tr>
                  ) : (
                    pageRows.map((b) => {
                      const count = b.batch_subjects_count ?? b.batchSubjectsCount ?? b.subjectCount ?? 0;
                      const badge = count > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
                      return (
                        <tr key={b.batchId}>
                          <td className="px-4 py-3 text-sm text-gray-900">{b.batchId}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="font-medium">{b.batchName}</div>
                            <div className="text-xs text-gray-500">{b.course?.shortName || b.course?.fullName || b.courseShortName || b.courseFullName || '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {b.whatsappGroupName ? (
                              <span className="text-gray-900">{b.whatsappGroupName}</span>
                            ) : (
                              <span className="text-gray-400 italic">Not set</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge}`}>
                              {count}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex flex-wrap gap-2">
                              <button
                                className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                onClick={() => fetchBatchSubjects(b)}
                              >
                                Show Assigned Subjects
                              </button>
                              <button
                                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                onClick={() => openAssignModal(b)}
                              >
                                Assign Subjects
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col items-center justify-between gap-3 md:flex-row">
              <div className="text-sm text-gray-600">
                Showing {pageRows.length > 0 ? start + 1 : 0}–{Math.min(end, filteredBatches.length)} of {filteredBatches.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
                <button
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {showSubjectsModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="relative w-full max-w-7xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">Assigned Subjects</h2>
                      <p className="mt-1 text-sm text-blue-100">
                        {selectedBatch?.batchName} - {selectedBatch?.course?.shortName || selectedBatch?.course?.fullName || selectedBatch?.courseShortName || selectedBatch?.courseFullName}
                      </p>
                      <p className="mt-1 text-xs text-blue-200">
                        Total Subjects: <span className="font-semibold">{batchSubjects.length}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowSubjectsModal(false); setSelectedBatch(null); setBatchSubjects([]); }}
                      className="rounded-full p-2 text-white transition-colors hover:bg-white hover:bg-opacity-20"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                  {loadingSubjects ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
                        <p className="mt-4 text-sm text-gray-600">Loading subjects...</p>
                      </div>
                    </div>
                  ) : batchSubjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <svg className="h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <p className="mt-4 text-lg font-medium text-gray-900">No subjects assigned</p>
                      <p className="mt-1 text-sm text-gray-600">This batch doesn't have any subjects assigned yet.</p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4 flex justify-end">
                        <button
                          onClick={downloadAsExcel}
                          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download as Excel
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Staff</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Start Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">End Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Expected Classes</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Semester</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Release Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Submission Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Late Submission</th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {batchSubjects.map((subject, index) => (
                              <tr key={subject.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{index + 1}</td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{subject.subjectName}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{subject.staffName || '—'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{subject.startDate}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{subject.endDate}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                    subject.status === 'Active' || subject.status === 'ongoing' ? 'bg-green-100 text-green-800' :
                                    subject.status === 'Inactive' || subject.status === 'not started' ? 'bg-gray-100 text-gray-800' :
                                    subject.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {subject.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-gray-900">{subject.expectedNumberOfClasses}</td>
                                <td className="px-4 py-3 text-sm text-center text-gray-900">{subject.semester}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{subject.assignmentReleaseDate || '—'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{subject.assignmentSubmissionDate || '—'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{subject.assignmentLateSubmissionDate || '—'}</td>
                                <td className="px-4 py-3 text-sm">
                                  <button
                                    onClick={() => handleDeleteSubject(subject.id)}
                                    className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                    title="Delete subject"
                                  >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {showAssignModal && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
              <div className="absolute inset-0 flex flex-col bg-white">
                <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">Assign Subjects to Batch</h2>
                      <p className="mt-1 text-sm text-indigo-100">
                        {selectedBatchForAssign?.batchName} — {selectedBatchForAssign?.course?.shortName || selectedBatchForAssign?.course?.fullName || selectedBatchForAssign?.courseShortName || selectedBatchForAssign?.courseFullName}
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowAssignModal(false); setSelectedBatchForAssign(null); }}
                      className="rounded-full p-2 text-white transition-colors hover:bg-white hover:bg-opacity-20"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {assignLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent" />
                        <p className="mt-4 text-sm text-gray-600">Loading subjects and staff...</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-5">
                        <div className="md:col-span-1">
                          <label className="mb-1 block text-sm font-medium text-gray-700">Batch Start Date</label>
                          <input type="date" value={calcStartDate} onChange={(e) => setCalcStartDate(e.target.value)} className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                        </div>
                        <div className="md:col-span-1">
                          <label className="mb-1 block text-sm font-medium text-gray-700">Number of Months</label>
                          <input type="number" min={1} value={calcMonths} onChange={(e) => setCalcMonths(e.target.value === '' ? '' : Number(e.target.value))} className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                        </div>
                        <div className="md:col-span-1">
                          <label className="mb-1 block text-sm font-medium text-gray-700">Batch End Date</label>
                          <input type="date" value={calcEndDate} onChange={(e) => setCalcEndDate(e.target.value)} className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                        </div>
                        <div className="md:col-span-1">
                          <label className="mb-1 block text-sm font-medium text-gray-700">Number of Semesters</label>
                          <input type="number" min={1} value={calcSemesters} onChange={(e) => setCalcSemesters(e.target.value === '' ? '' : Number(e.target.value))} className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                        </div>
                        <div className="flex items-end gap-2 md:col-span-1">
                          <button onClick={applyCalculation} className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1">Apply calculation</button>
                        </div>
                      </div>

                      <div className="mb-4 flex items-center justify-between">
                        <div className="text-sm text-gray-600">Subjects from course: <span className="font-medium">{courseSubjects.length}</span></div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const semesters = typeof calcSemesters === 'number' ? calcSemesters : parseInt(String(calcSemesters || '0'), 10);
                              if (!calcStartDate || !calcEndDate || !semesters || semesters < 1) {
                                Swal.fire({ icon: 'info', title: 'Set calculation first', text: 'Please fill start date, end date and semesters to preview calculation.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
                                return;
                              }
                              setShowSemCalcModal(true);
                            }}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Show Semester Calculation
                          </button>
                          <button onClick={saveAssignments} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1">Save Assignments</button>
                          <button onClick={() => { setShowAssignModal(false); setSelectedBatchForAssign(null); }} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
                        </div>
                      </div>

                      {showSemCalcModal && (
                        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50 p-4">
                          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl">
                            <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="text-xl font-bold text-white">Semester Calculation</h3>
                                  <p className="mt-1 text-xs text-indigo-100">Based on current Start Date, End Date and Number of Semesters</p>
                                </div>
                                <button
                                  onClick={() => setShowSemCalcModal(false)}
                                  className="rounded-full p-2 text-white transition-colors hover:bg-white hover:bg-opacity-20"
                                >
                                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                              {(() => {
                                const semesters = typeof calcSemesters === 'number' ? calcSemesters : parseInt(String(calcSemesters || '0'), 10);
                                const ranges = computeSemesterRanges(calcStartDate, calcEndDate, semesters);
                                if (!calcStartDate || !calcEndDate || !semesters || semesters < 1) {
                                  return (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">Fill calculation fields to preview semester split.</div>
                                  );
                                }
                                return (
                                  <>
                                    <div className="mb-4 text-sm text-gray-700">
                                      <div className="flex flex-wrap items-center gap-3">
                                        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-indigo-800">
                                          <span className="font-semibold">Batch Start Date:</span> {calcStartDate}
                                        </div>
                                        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-indigo-800">
                                          <span className="font-semibold">Batch End Date:</span> {calcEndDate}
                                        </div>
                                        <div className="rounded-md border border-gray-200 bg-gray-100 px-3 py-1 text-gray-800">
                                          <span className="font-semibold">Semesters:</span> {semesters}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                      <div className="mb-2 text-sm font-medium text-gray-800">Temporary schedule calculation</div>
                                      <div className="flex flex-wrap items-center gap-4 text-sm">
                                        <label className="inline-flex items-center gap-2">
                                          <input
                                            type="radio"
                                            name="sem-calc-mode"
                                            checked={semCalcMode === 'weekend'}
                                            onChange={() => setSemCalcMode('weekend')}
                                          />
                                          <span>Weekend (2 days/week)</span>
                                        </label>
                                        <label className="inline-flex items-center gap-2">
                                          <input
                                            type="radio"
                                            name="sem-calc-mode"
                                            checked={semCalcMode === 'weekday'}
                                            onChange={() => setSemCalcMode('weekday')}
                                          />
                                          <span>Weekday</span>
                                        </label>
                                        {semCalcMode === 'weekday' && (
                                          <div className="inline-flex items-center gap-2">
                                            <label className="text-gray-700">Classes per week</label>
                                            <input
                                              type="number"
                                              min={1}
                                              max={7}
                                              value={semCalcClassesPerWeek}
                                              onChange={(e) => setSemCalcClassesPerWeek(Math.max(1, Math.min(7, Number(e.target.value) || 1)))}
                                              className="w-24 rounded-md border border-gray-300 px-2 py-1 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                            />
                                          </div>
                                        )}
                                        {semCalcMode === 'weekend' && (
                                          <div className="text-gray-600">Using default 2 classes/week</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Semester</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Start Date</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">End Date</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Months</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Est. Classes</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Possible Hours</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                          {ranges.map((r) => {
                                            const estClasses = semCalcMode === 'weekend'
                                              ? Math.round((diffDays(r.start, r.end) / 7) * 2)
                                              : Math.round((diffDays(r.start, r.end) / 7) * semCalcClassesPerWeek);
                                            const possibleHours = Math.round(estClasses * 2 * 10) / 10;
                                            return (
                                              <tr key={r.sem} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{r.sem}</td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{r.start}</td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{r.end}</td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{monthsApprox(r.start, r.end)}</td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{estClasses}</td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{possibleHours}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>

                            <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-gray-50 px-6 py-3">
                              <button
                                onClick={() => setShowSemCalcModal(false)}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <input type="checkbox" aria-label="Select all" checked={assignRows.length > 0 && assignRows.every((r) => selectedIds.has(r.courseSubjectId))} onChange={toggleSelectAll} />
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Staff</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Start</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">End</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Expected Classes</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Semester</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Release</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Submission</th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Late</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {assignRows.length === 0 ? (
                              <tr><td colSpan={12} className="px-3 py-4 text-center text-sm text-gray-500">No subjects found for the course.</td></tr>
                            ) : assignRows.map((row, idx) => (
                              <tr
                                key={`${row.courseSubjectId}-${idx}`}
                                className={`${selectedIds.has(row.courseSubjectId) ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'}`}
                              >
                                <td className="px-3 py-2 text-sm">
                                  <input
                                    type="checkbox"
                                    aria-label={`Select ${row.subjectName}`}
                                    checked={selectedIds.has(row.courseSubjectId)}
                                    onChange={() => toggleSelectRow(row.courseSubjectId)}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">{idx + 1}</td>
                                <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.subjectName}</td>
                                <td className="px-3 py-2 text-sm">
                                  <select
                                    className="min-w-[180px] rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    value={row.staffId ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? null : Number(e.target.value);
                                      setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, staffId: val } : r)));
                                    }}
                                  >
                                    <option value="">— Select —</option>
                                    {staffOptions.map((s) => (
                                      <option key={s.staffId} value={s.staffId}>{s.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input type="date" value={row.startDate} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, startDate: e.target.value } : r)))} className="rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input type="date" value={row.endDate} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, endDate: e.target.value } : r)))} className="rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <select value={row.status} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, status: e.target.value } : r)))} className="rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                                    {statusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                  </select>
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input type="number" min={0} value={row.expectedNumberOfClasses ?? ''} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, expectedNumberOfClasses: e.target.value === '' ? null : Number(e.target.value) } : r)))} className="w-24 rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input
                                    type="number"
                                    min={1}
                                    max={calcSemesters || 20}
                                    value={row.semester}
                                    onChange={(e) => {
                                      const { value } = e.target;

                                      // Do not allow changing semester until calculation
                                      // has been applied, so that date ranges are known.
                                      if (!calcApplied) {
                                        Swal.fire({
                                          icon: 'info',
                                          title: 'Apply calculation first',
                                          text: 'Please set the batch start date, months and semesters, then click "Apply calculation" before editing semesters.',
                                          toast: true,
                                          position: 'top-end',
                                          showConfirmButton: false,
                                          timer: 3500,
                                        });
                                        return;
                                      }

                                      // If semester is cleared, also clear this row's dates.
                                      if (value === '') {
                                        setAssignRows((prev) => prev.map((r, i) => (i === idx
                                          ? {
                                            ...r,
                                            semester: '',
                                            startDate: '',
                                            endDate: '',
                                            assignmentReleaseDate: null,
                                            assignmentSubmissionDate: null,
                                            assignmentLateSubmissionDate: null,
                                          }
                                          : r)));
                                        return;
                                      }

                                      const totalSemesters = typeof calcSemesters === 'number'
                                        ? calcSemesters
                                        : parseInt(String(calcSemesters || '0'), 10);
                                      const raw = Number(value);
                                      const newSem = Math.max(1, totalSemesters ? Math.min(raw, totalSemesters) : raw);

                                      // If we don't have full calculation info yet, only update the semester number.
                                      if (!calcStartDate || !calcEndDate || !totalSemesters || totalSemesters < 1) {
                                        setAssignRows((prev) => prev.map((r, i) => (i === idx
                                          ? { ...r, semester: newSem }
                                          : r)));
                                        return;
                                      }

                                      const ranges = computeSemesterRanges(calcStartDate, calcEndDate, totalSemesters);
                                      const range = ranges[newSem - 1];

                                      setAssignRows((prev) => prev.map((r, i) => {
                                        if (i !== idx) return r;

                                        if (!range) {
                                          return {
                                            ...r,
                                            semester: newSem,
                                            startDate: '',
                                            endDate: '',
                                            assignmentReleaseDate: null,
                                            assignmentSubmissionDate: null,
                                            assignmentLateSubmissionDate: null,
                                          };
                                        }

                                        const release = addMonths(range.start, 1);
                                        const submission = addDays(range.end, 25);
                                        const late = addDays(submission, 14);

                                        return {
                                          ...r,
                                          semester: newSem,
                                          startDate: range.start,
                                          endDate: range.end,
                                          assignmentReleaseDate: release || null,
                                          assignmentSubmissionDate: submission || null,
                                          assignmentLateSubmissionDate: late || null,
                                        };
                                      }));
                                    }}
                                    className="w-20 rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input type="date" value={row.assignmentReleaseDate || ''} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, assignmentReleaseDate: e.target.value || null } : r)))} className="rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input type="date" value={row.assignmentSubmissionDate || ''} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, assignmentSubmissionDate: e.target.value || null } : r)))} className="rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input type="date" value={row.assignmentLateSubmissionDate || ''} onChange={(e) => setAssignRows((prev) => prev.map((r, i) => (i === idx ? { ...r, assignmentLateSubmissionDate: e.target.value || null } : r)))} className="rounded-lg border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
